import { useCallback, useEffect, useRef, useState } from 'react';

const BFF_URL = import.meta.env.VITE_BFF_URL ?? 'http://localhost:3600';

// ─── Protocol types — mirrors go-admin-bff ws/session.go ─────────────────────

export interface WsMessage {
  type: string;
  payload?: unknown;
  seq?: number;
}

export interface ChatChunkPayload {
  content: string;
  seq: number;
  done?: boolean;
}

export type WsStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

export interface UseWebSocketOptions {
  token: string;
  onMessage?: (msg: WsMessage) => void;
  onStatusChange?: (status: WsStatus) => void;
  reconnectDelay?: number;
  maxReconnects?: number;
}

export interface UseWebSocketReturn {
  status: WsStatus;
  send: (msg: WsMessage) => void;
  connect: () => void;
  disconnect: () => void;
}

// ─── Derive WebSocket URL from BFF_URL ───────────────────────────────────────

function toWsUrl(bffUrl: string, token: string): string {
  const base = bffUrl.replace(/^http/, 'ws');
  return `${base}/ws/chat?token=${encodeURIComponent(token)}`;
}

// Calling close() on a CONNECTING socket triggers a browser warning.
function closeGracefully(ws: WebSocket, code = 1000, reason = 'close'): void {
  if (ws.readyState === WebSocket.CONNECTING) {
    ws.onopen = () => ws.close(code, reason);
    ws.onerror = null;
  } else {
    ws.close(code, reason);
  }
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useWebSocket({
  token,
  onMessage,
  onStatusChange,
  reconnectDelay = 2000,
  maxReconnects = 5,
}: UseWebSocketOptions): UseWebSocketReturn {
  const [status, setStatus] = useState<WsStatus>('disconnected');
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectCount = useRef(0);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intentionalClose = useRef(false);
  const seqRef = useRef(0);

  const updateStatus = useCallback(
    (next: WsStatus) => {
      setStatus(next);
      onStatusChange?.(next);
    },
    [onStatusChange],
  );

  const disconnect = useCallback(() => {
    intentionalClose.current = true;
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }
    const ws = wsRef.current;
    wsRef.current = null;
    if (ws) closeGracefully(ws, 1000, 'client disconnect');
    updateStatus('disconnected');
  }, [updateStatus]);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    if (!token) return;

    intentionalClose.current = false;
    updateStatus('connecting');

    const ws = new WebSocket(toWsUrl(BFF_URL, token));
    wsRef.current = ws;

    ws.onopen = () => {
      reconnectCount.current = 0;
      console.log('[WS ↕] connected');
      updateStatus('connected');
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data as string) as WsMessage;
        const isChunk = msg.type === 'stream.chunk';
        const isDone = isChunk && (msg.payload as { done?: boolean } | undefined)?.done;
        if (!isChunk || isDone) {
          console.log('[WS ↓]', msg.type, msg);
        }
        onMessage?.(msg);
      } catch {
        // ignore malformed frames
      }
    };

    ws.onerror = () => {
      console.warn('[WS ✕] error');
      updateStatus('error');
    };

    ws.onclose = (event) => {
      wsRef.current = null;
      console.log('[WS ↕] disconnected', event.code, event.reason);
      if (intentionalClose.current || event.code === 1000) {
        updateStatus('disconnected');
        return;
      }
      updateStatus('error');
      if (reconnectCount.current < maxReconnects) {
        reconnectCount.current += 1;
        reconnectTimer.current = setTimeout(() => {
          connect();
        }, reconnectDelay * reconnectCount.current);
      }
    };
  }, [token, onMessage, updateStatus, maxReconnects, reconnectDelay]);

  const send = useCallback((msg: WsMessage) => {
    if (wsRef.current?.readyState !== WebSocket.OPEN) return;
    seqRef.current += 1;
    wsRef.current.send(JSON.stringify({ ...msg, seq: seqRef.current }));
  }, []);

  useEffect(() => {
    return () => {
      intentionalClose.current = true;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      const ws = wsRef.current;
      wsRef.current = null;
      if (ws) closeGracefully(ws, 1000, 'unmount');
    };
  }, []);

  return { status, send, connect, disconnect };
}
